/* eslint-disable no-console */
import React, {useEffect, useRef, useState} from 'react'
import {PageTitle} from 'layout/master-layout'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import UploadIcon from '../../../img/uploadIcon.svg'
import {toast} from 'react-toastify'
import {useLocation, useNavigate} from 'react-router-dom'
import {
 fetchBulkUploadOrganisation,
 getBulkUploadTemplateApi,
} from 'utils/api/auth/bulkUploadService'
import {fixNextPageUrl} from 'utils/helper'
import {
 fetchDistrict,
 fetchState,
} from 'utils/api/auth/locationService'
import DynamicModal from '../common/DynamicModal'

interface IDropdownOption {
 key: string
 value: string
}

const BulkUploadForm = () => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const location = useLocation()
 const stateData = location.state
 const aggregatorId = stateData?.aggregatorId
 const aggregatorName = stateData?.aggregatorName
 const [showErrorModal, setShowErrorModal] = useState(false)
 const [stateSearch, setStateSearch] = useState('')
 const [districtSearch, setDistrictSearch] = useState('')
 const [aggregatorSearch, setAggregatorSearch] =
  useState('')
 const isReupload = Boolean(aggregatorId)
 const fileRef = useRef<HTMLInputElement | null>(null)
 const [selectedFile, setSelectedFile] =
  useState<File | null>(null)
 const [parentId] = useState(stateData?.uploadId || '')
 const [stateOpen, setStateOpen] = useState(false)
 const [districtOpen, setDistrictOpen] = useState(false)
 const [aggregatorOpen, setAggregatorOpen] = useState(false)
 const stateRef = useRef<HTMLDivElement>(null)
 const districtRef = useRef<HTMLDivElement>(null)
 const aggregatorRef = useRef<HTMLDivElement>(null)
 const [selectedState, setSelectedState] = useState('')
 const [selectedStateId, setSelectedStateId] = useState<
  number | null
 >(null)
 const [isDragging, setIsDragging] = useState(false)
 const [selectedAggregatorKey, setSelectedAggregatorKey] =
  useState('')
 const [selectedDistrict, setSelectedDistrict] =
  useState('')
 const [selectedDistrictId, setSelectedDistrictId] =
  useState<number | null>(null)
 const [selectedAggregator, setSelectedAggregator] =
  useState('')
 const [stateOptions, setStateOptions] = useState<
  IDropdownOption[]
 >([])
 const [districtOptions, setDistrictOptions] = useState<
  IDropdownOption[]
 >([])
 const [aggregatorOptions, setAggregatorOptions] = useState<
  IDropdownOption[]
 >([])

 const handleDownloadTemplate = async () => {
  const res = await getBulkUploadTemplateApi(
   'farmer-bulk-upload'
  )

  if (res?.success && res.data?.template_url) {
   const safeUrl =
    fixNextPageUrl(res.data.template_url) ?? ''
   if (!safeUrl) return
   const anchor = document.createElement('a')
   anchor.href = safeUrl
   anchor.target = '_blank'
   anchor.rel = 'noopener noreferrer'
   document.body.appendChild(anchor)
   anchor.click()
   anchor.remove()
  }
 }

 const handleSubmit = () => {
  if (!selectedFile || !selectedAggregator) return

  const validExtensions = ['csv', 'xlsx']

  const extension =
   selectedFile.name.split('.').pop()?.toLowerCase() || ''

  const maxFileSize = 10 * 1024 * 1024

  const isValidExtension =
   validExtensions.includes(extension)

  const isValidSize = selectedFile.size <= maxFileSize

  if (!isValidExtension || !isValidSize) {
   setShowErrorModal(true)
   return
  }

  navigate('/activity-management/bulk-upload-management', {
   replace: true,
   state: {
    startUpload: true,
    uploadPayload: {
     file: selectedFile,
     aggregatorKey: selectedAggregatorKey,
     parentId,
    },
   },
  })
 }
 const handleFileClick = () => {
  if (!selectedAggregator) return
  fileRef.current?.click()
 }

 const handleFileChange = (
  e: React.ChangeEvent<HTMLInputElement>
 ) => {
  if (e.target.files && e.target.files[0]) {
   setSelectedFile(e.target.files[0])
   e.target.value = ''
  }
 }
 const handleDragOver = (
  e: React.DragEvent<HTMLDivElement>
 ) => {
  e.preventDefault()
  e.stopPropagation()

  if (!selectedAggregator) return

  if (!isDragging) {
   setIsDragging(true)
  }
 }

 const handleDragLeave = (
  e: React.DragEvent<HTMLDivElement>
 ) => {
  e.preventDefault()
  e.stopPropagation()

  setIsDragging(false)
 }

 const handleDrop = (
  e: React.DragEvent<HTMLDivElement>
 ) => {
  e.preventDefault()
  e.stopPropagation()

  setIsDragging(false)

  if (!selectedAggregator) return

  const droppedFiles = e.dataTransfer.files

  if (!droppedFiles || droppedFiles.length === 0) {
   return
  }

  const file = droppedFiles[0]

  const validExtensions = ['csv', 'xlsx']

  const extension =
   file.name.split('.').pop()?.toLowerCase() || ''

  if (!validExtensions.includes(extension)) {
   toast.error('Only CSV and XLSX files are supported')
   return
  }

  setSelectedFile(file)

  if (fileRef.current) {
   const dataTransfer = new DataTransfer()

   dataTransfer.items.add(file)

   fileRef.current.files = dataTransfer.files
  }
 }

 const renderDropdown = ({
  label,
  placeholder,
  value,
  options,
  open,
  setOpen,
  setValue,
  dropdownRef,
  disabled = false,
  searchTerm,
  setSearchTerm,
 }: {
  label: string
  placeholder: string
  value: string
  options: IDropdownOption[]
  open: boolean
  setOpen: (value: boolean) => void
  setValue: (value: string) => void
  dropdownRef: React.RefObject<HTMLDivElement>
  disabled?: boolean
  searchTerm: string
  setSearchTerm: (value: string) => void
 }) => {
  const filteredOptions = options.filter((item) =>
   item.value
    .toLowerCase()
    .includes(searchTerm.toLowerCase())
  )
  return (
   <div ref={dropdownRef} className='relative w-full'>
    <div
     onClick={() => {
      if (!disabled) {
       setOpen(!open)
      }
     }}
     className={`h-[78px] rounded-xl border px-5 flex flex-col justify-center cursor-pointer transition-all
      ${
       value
        ? 'bg-[#F5F8FA] border-[#DCE3EA]'
        : 'bg-white border-[#DCE3EA]'
      }
      ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
     `}
    >
     <div className='flex items-center justify-between'>
      <div className='flex-1 min-w-0 overflow-hidden'>
       <p
        className={`text-[14px] font-medium ${
         disabled ? 'text-[#C5CED6]' : 'text-[#4E83E4]'
        }`}
       >
        {label}
        <span className='text-[#E03137]'>*</span>
       </p>

       <input
        type='text'
        value={open ? searchTerm : value}
        placeholder={placeholder}
        onChange={(e) => {
         if (disabled) return
         setSearchTerm(e.target.value)

         if (!open) {
          setOpen(true)
         }
        }}
        onClick={(e) => {
         e.stopPropagation()

         if (!disabled) {
          setOpen(true)
         }
        }}
        className={`mt-1 w-full bg-transparent outline-none text-[18px]
 ${
  disabled
   ? 'text-[#C5CED6] cursor-not-allowed pointer-events-none'
   : value
   ? 'text-[#33393D]'
   : 'text-[#98A2B3]'
 }`}
       />
      </div>

      <svg
       className={`w-6 h-6 transition-transform ${
        open ? 'rotate-180' : ''
       }`}
       viewBox='0 0 20 20'
       fill='none'
       stroke='currentColor'
      >
       <path d='M6 8l4 4 4-4' strokeWidth='2' />
      </svg>
     </div>
    </div>

    {open && (
     <div className='absolute top-[84px] left-0 w-full max-h-[320px] overflow-y-auto rounded-2xl bg-[#F5F8FA] shadow-xl z-50'>
      <div className='max-h-[260px] overflow-y-auto'>
       {filteredOptions.length > 0 ? (
        filteredOptions.map((item, index) => (
         <div
          key={item.key}
          onClick={() => {
           setValue(item.value)
           setSearchTerm('')
           setOpen(false)
          }}
          className={`flex items-center gap-4 px-5 py-5 cursor-pointer hover:bg-[#EEF3F7]
              ${
               index !== filteredOptions.length - 1
                ? 'border-b border-[#E3E8EF]'
                : ''
              }
            `}
         >
          <input
           type='radio'
           checked={value === item.value}
           readOnly
           className='w-6 h-6 shrink-0 cursor-pointer appearance-none rounded-full border-2 border-[#3F76FF] checked:bg-[#3F76FF] relative
              after:content-[""] after:absolute after:w-2.5 after:h-2.5 after:bg-white after:rounded-full after:top-1/2 after:left-1/2
              after:-translate-x-1/2 after:-translate-y-1/2 after:scale-0 checked:after:scale-100'
          />

          <p className='text-[18px] text-[#33393D] font-medium break-words'>
           {item.value}
          </p>
         </div>
        ))
       ) : (
        <div className='px-5 py-6 text-center text-[#98A2B3] text-[14px]'>
         No data found
        </div>
       )}
      </div>
     </div>
    )}
   </div>
  )
 }

 useEffect(() => {
  const handler = (e: MouseEvent) => {
   if (
    stateRef.current &&
    !stateRef.current.contains(e.target as Node)
   ) {
    setStateOpen(false)
   }

   if (
    districtRef.current &&
    !districtRef.current.contains(e.target as Node)
   ) {
    setDistrictOpen(false)
   }

   if (
    aggregatorRef.current &&
    !aggregatorRef.current.contains(e.target as Node)
   ) {
    setAggregatorOpen(false)
   }
  }
  document.addEventListener('mousedown', handler)

  return () =>
   document.removeEventListener('mousedown', handler)
 }, [])

 useEffect(() => {
  if (isReupload) {
   setSelectedAggregator(aggregatorName || '')
   setSelectedAggregatorKey(String(aggregatorId || ''))
  }
 }, [aggregatorId, aggregatorName, isReupload])

 useEffect(() => {
  const loadOrganisations = async () => {
   if (!selectedStateId || !selectedDistrictId) {
    setAggregatorOptions([])
    return
   }

   try {
    const res = await fetchBulkUploadOrganisation({
     type: 'PACS,BLFC,FPO',
     state: selectedStateId,
     district: selectedDistrictId,
    })

    if (Array.isArray(res)) {
     const formattedData = res.map(
      (item: {id: number; name: string}) => ({
       key: String(item.id),
       value: item.name,
      })
     )

     setAggregatorOptions(formattedData)
    }
   } catch (err) {
    setAggregatorOptions([])
   }
  }

  void loadOrganisations()
 }, [selectedStateId, selectedDistrictId])

 useEffect(() => {
  const loadStates = async () => {
   const res = await fetchState()

   if (res) {
    const formattedStates = res.map(
     (item: {id: number; name: string}) => ({
      key: String(item.id),
      value: item.name,
     })
    )

    setStateOptions(formattedStates)
   }
  }

  void loadStates()
 }, [])

 useEffect(() => {
  const loadDistricts = async () => {
   if (!selectedStateId) {
    setDistrictOptions([])
    return
   }

   const res = await fetchDistrict(selectedStateId)

   if (res) {
    const districtData = res.map(
     (item: {id: number; name: string}) => ({
      key: String(item.id),
      value: item.name,
     })
    )

    setDistrictOptions(districtData)
   }
  }

  void loadDistricts()
 }, [selectedStateId])

 return (
  <>
   <div className='container px-[72px] pt-6 pb-10 bg-white min-h-full'>
    <PageTitle
     breadcrumbContainerClass='flex text-[#5A6066] text-sm items-center pb-6 border-b border-[#E6EBF0]'
     breadcrumbs={[
      {
       title: t('breadCrumbs.home'),
       path: '/home-page',
       isActive: false,
      },
      {
       title: t('breadCrumbs.activityManagement'),
       path: '/activity-management',
       isActive: false,
      },
      {
       title: t('breadCrumbs.bulkUploadManagement'),
       path: '/activity-management/bulk-upload-management',
       isActive: false,
      },
      {
       title: t('breadCrumbs.bulkUpload'),
       path: '',
       isActive: true,
      },
     ]}
    >
     Bulk Upload
    </PageTitle>
    <div className='flex items-center justify-between align-center mt-8'>
     <h2 className=' text-[20px] font-[700]  text-[#2F2F2F] '>
      {t('bulkUpload.title')}
     </h2>
     <div className='flex flex-col items-center justify-center'>
      <button
       onClick={handleDownloadTemplate}
       className={`
  min-w-[100px] max-w-[328px] h-[54px] px-4 flex items-center justify-center gap-2 rounded-[12px] border overflow-hidden text-center text-ellipsis whitespace-nowrap text-[16px] font-[600]  transition-all
   border-[#4E83E4] bg-[#F5FAFF] text-[#4E83E4] cursor-pointer
   
 `}
      >
       <KTSVG
        path='/media/logos/download.svg'
        className={`w-5 h-5 
        'text-[#4E83E4]'

       `}
       />

       <span className={'text-[#4E83E4]'}>
        {t('bulkUpload.downloadTemplate')}
       </span>
      </button>
      <p className='mt-2 text-center'>
       {t('bulkUpload.mandatoryColumns')}
       <span className='text-red'>*</span>
      </p>
     </div>
    </div>
    {!isReupload && (
     <>
      <div className='mt-6 border border-[#DCE3EA] rounded-2xl p-5'>
       <h3 className='mb-4 text-[16px] font-[600] text-[#33393D]'>
        {t('bulkUpload.geography')}
       </h3>

       <div className='flex flex-wrap gap-4'>
        <div className='w-full md:w-[32%]'>
         {renderDropdown({
          label: t('bulkUpload.state'),
          placeholder: t('bulkUpload.statePlaceholder'),
          value: selectedState,
          options: stateOptions,
          open: stateOpen,
          setOpen: setStateOpen,
          setValue: (val) => {
           const selectedObj = stateOptions.find(
            (item) => item.value === val
           )

           setSelectedState(val)
           setSelectedStateId(Number(selectedObj?.key))

           setSelectedDistrict('')
           setSelectedDistrictId(null)

           setSelectedAggregator('')
           setSelectedAggregatorKey('')
          },
          dropdownRef: stateRef,
          searchTerm: stateSearch,
          setSearchTerm: setStateSearch,
         })}
        </div>

        <div className='w-full md:w-[32%]'>
         {renderDropdown({
          label: t('bulkUpload.district'),
          placeholder: t('bulkUpload.districtPlaceholder'),
          value: selectedDistrict,
          options: districtOptions,
          open: districtOpen,
          setOpen: setDistrictOpen,
          setValue: (val) => {
           const selectedObj = districtOptions.find(
            (item) => item.value === val
           )

           setSelectedDistrict(val)
           setSelectedDistrictId(Number(selectedObj?.key))

           setSelectedAggregator('')
           setSelectedAggregatorKey('')
          },
          dropdownRef: districtRef,
          disabled: !selectedState,
          searchTerm: districtSearch,
          setSearchTerm: setDistrictSearch,
         })}
        </div>
       </div>
      </div>

      <div className='mt-6 border border-[#DCE3EA] rounded-2xl p-5'>
       <h3
        className={`mb-4 text-[16px] font-[600] ${
         selectedDistrict
          ? 'text-[#33393D]'
          : 'text-[#C5CED6]'
        }`}
       >
        {t('bulkUpload.aggregator')}
       </h3>

       <div className='flex flex-wrap gap-4'>
        <div className='w-full md:w-[32%]'>
         {renderDropdown({
          label: t('bulkUpload.columns.aggregator'),
          placeholder: t('bulkUpload.aggregator'),
          value: selectedAggregator,
          options: aggregatorOptions,
          open: aggregatorOpen,
          setOpen: setAggregatorOpen,
          setValue: (val) => {
           const selectedObj = aggregatorOptions.find(
            (item) => item.value === val
           )

           setSelectedAggregator(val)
           setSelectedAggregatorKey(selectedObj?.key || '')
          },
          dropdownRef: aggregatorRef,
          disabled: isReupload || !selectedDistrict,
          searchTerm: aggregatorSearch,
          setSearchTerm: setAggregatorSearch,
         })}
        </div>
       </div>
      </div>
     </>
    )}

    <div className='mt-8'>
     <label className='text-[16px] font-[600] text-[#33393D] leading-[143%] tracking-[-0.25px] font-["Noto_Sans"]'>
      {t('bulkUpload.uploadFile')}
      <span className='text-[#E03137]'>*</span>
     </label>

     <div
      onClick={() => {
       handleFileClick()
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`mt-4 flex flex-col items-center justify-center rounded-[16px] border border-dashed transition-all min-h-[210px] p-8 gap-2 cursor-pointer
    ${
     isDragging
      ? 'border-[#4E83E4] bg-[#EAF2FF]'
      : selectedFile
      ? 'border-[#7BC144] bg-[#EEF8E8]'
      : selectedAggregator
      ? 'border-[#7DA7F7] bg-[#F5F8FC]'
      : 'border-[#D7DFEA] bg-[#F5F8FA] cursor-not-allowed'
    }
  `}
     >
      <KTSVG
       path={
        selectedFile
         ? '/media/icons/csvFileUpload.svg'
         : UploadIcon
       }
      />

      <p
       title={selectedFile?.name}
       className={`  max-w-[420px] truncate whitespace-nowrap overflow-hidden text-center text-[15px] font-[700] leading-[22px]
        ${
         selectedFile
          ? 'text-[#4B5563]'
          : selectedAggregator
          ? 'text-[#5A6066]'
          : 'text-[#C5CED6]'
        }
      `}
      >
       {selectedFile
        ? selectedFile.name
        : t('bulkUpload.downloadTheTemplate')}
      </p>

      <p className=' text-[14px] text-[#7B8794] text-center'>
       {t('bulkUpload.dragAndDrop')}
      </p>

      <button
       type='button'
       className={`mt-2 h-[42px] px-6 rounded-[8px] text-[15px] font-[600] flex items-center justify-center self-center transition-all
        ${
         selectedFile || selectedAggregator
          ? 'bg-[#4E83E4] text-white'
          : 'bg-[#DCE3EA] text-[#98A2B3]'
        }
      `}
      >
       {t('bulkUpload.chooseFile')}
      </button>

      <p className='mt-2 text-[12px] text-[#98A2B3]'>
       {t('bulkUpload.fileSupport')}
      </p>
     </div>

     <input
      ref={fileRef}
      type='file'
      className='hidden'
      accept='.csv,.xlsx'
      onChange={handleFileChange}
      onClick={(e) => {
       e.stopPropagation()
      }}
     />
    </div>

    <div className='mt-10 border-t border-[#E6EBF0] pt-8 flex justify-end gap-5'>
     <button
      onClick={handleSubmit}
      className={`flex h-[48px] min-w-[100px] max-w-[328px] px-4 py-3 justify-center items-center rounded-[8px] text-[16px] font-[600] transition-all
      ${
       selectedFile && selectedAggregator
        ? 'bg-[#4E83E4] text-white'
        : 'bg-[#C5CED6] text-white cursor-not-allowed'
      }
      `}
     >
      {t('bulkUpload.submitUpload')}
     </button>
    </div>
   </div>
   {showErrorModal && (
    <DynamicModal
     title={t('bulkUpload.fileUploadFailed')}
     description={t('bulkUpload.errMessage')}
     onConfirm={() => {
      setShowErrorModal(false)
      handleFileClick()
     }}
     onCancel={() => {
      setShowErrorModal(false)
     }}
     textConfirm={t('bulkUpload.retryUpload')}
     textCancel={t('bulkUpload.cancel')}
    />
   )}
  </>
 )
}

export default BulkUploadForm
